import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEOHead } from "@/components/shared/SEOHead";
import {
  Trees,
  Flower2,
  Fence,
  Sprout,
  LeafyGreen,
  ArrowRight,
  Check,
  Quote,
  Star,
  MessageSquare,
  FileText,
  Ruler,
  Hammer,
  CheckCircle,
  Search,
  Phone,
  Mail,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

const services = [
  { icon: Trees, title: "Gartengestaltung", description: "Individuelle Gartenplanung und -gestaltung. Von der Idee bis zum fertigen Traum-Garten." },
  { icon: Fence, title: "Pflasterarbeiten", description: "Professionelle Pflaster- und Wegearbeiten f\u00fcr Einfahrten, Terrassen und Gehwege." },
  { icon: Flower2, title: "Bepflanzung", description: "Fachgerechte Bepflanzung mit standortgerechten Geh\u00f6lzen, Stauden und Gr\u00e4sern." },
  { icon: Sprout, title: "Rasen & Rollrasen", description: "Rasenans\u00e4ung, Rollrasenverlegung und professionelle Rasenpflege." },
];

const reviews = [
  { quote: "Wir haben ein Haus erworben, bei dem die Terrasse und der Weg komplett neu gepflastert werden mussten. Das Unternehmen ist bekannt durch seine g\u00fcnstigen Preise und dennoch qualitativ guter und sauberer Arbeit. Wir k\u00f6nnen die Firma Freitag wirklich JEDEM empfehlen!", author: "Florian Fricke", company: "Google Bewertung" },
  { quote: "Einfach nur top. Firma Freitag hat sich um unsere Badezimmer-Renovierung gek\u00fcmmert. Es hat alles hervorragend geklappt. Bin sehr zufrieden. Immer wieder gerne.", author: "Carmen Behrens", company: "Google Bewertung" },
  { quote: "Lief alles super. Revisionsschacht im Vorgarten wurde preisg\u00fcnstig realisiert. Immer wieder. Beste Gr\u00fc\u00dfe", author: "Fa Schneider", company: "Google Bewertung" },
  { quote: "Super nett und hilfsbereit. Schnelle und zuverl\u00e4ssige Abwicklung. Vielen Dank.", author: "Christian J\u00e4schke", company: "Google Bewertung" },
];

const faqs = [
  { question: "Welche Gartenarbeiten bieten Sie an?", answer: "Wir bieten das volle Spektrum: Gartengestaltung, Pflasterarbeiten, Bepflanzung, Rasenverlegung und laufende Gr\u00fcnpflege." },
  { question: "Gestalten Sie auch kleine Privatg\u00e4rten?", answer: "Ja, wir gestalten G\u00e4rten jeder Gr\u00f6\u00dfe \u2013 vom kleinen Reihenhausgarten bis zur gro\u00dffl\u00e4chigen Gewerbeau\u00dfenanlage." },
  { question: "Bieten Sie auch Winterdienst an?", answer: "Ja, wir bieten einen zuverl\u00e4ssigen Winterdienst f\u00fcr Gewerbe- und Privatkunden an." },
  { question: "Wie schnell k\u00f6nnen Sie beginnen?", answer: "Je nach Auftragsumfang und Jahreszeit k\u00f6nnen wir oft innerhalb von 2-4 Wochen mit den Arbeiten beginnen." },
];

const steps = [
  { icon: MessageSquare, title: "Beratung", desc: "Erstgespr\u00e4ch und Gartenbegehung.", number: "01" },
  { icon: FileText, title: "Angebot", desc: "Detaillierte Kalkulation.", number: "02" },
  { icon: Ruler, title: "Planung", desc: "Kreative Gartenplanung.", number: "03" },
  { icon: Hammer, title: "Umsetzung", desc: "Fachgerechte Ausf\u00fchrung.", number: "04" },
  { icon: CheckCircle, title: "Abnahme", desc: "\u00dcbergabe und Pflegetipps.", number: "05" },
];

/* ===== GALABAU LIGHT THEME PAGE ===== */
export default function GalabauPage() {
  return (
    <div className="theme-galabau-light">
      <SEOHead
        title="Galabau Braunschweig | Gartengestaltung, Pflasterarbeiten & Grünpflege | Freitag-Gruppe"
        description="Freitag Galabau in Braunschweig: Professionelle Gartengestaltung, Pflasterarbeiten, Bepflanzung und Grünflächenpflege. Über 15 Jahre Erfahrung im Garten- und Landschaftsbau. Jetzt Beratung anfragen!"
        canonical="/galabau"
        schemaKey="galabau"
      />
      <GalabauHero />
      <GalabauServices />
      <GalabauSEO />
      <GalabauReferenzen />
      <GalabauProzess />
      <GalabauReviews />
      <GalabauKontakt />
      <GalabauFAQ />
    </div>
  );
}

/* Hero */
function GalabauHero() {
  return (
    <section className="relative pt-20 min-h-[85vh] flex flex-col overflow-hidden bg-background">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(/images/galabau-hero.jpg)` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/60" />

      {/* Top accent */}
      <div className="absolute top-0 left-0 right-0 h-1 gradient-green z-20" />

      {/* Decorative */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[1]">
        <div className="absolute top-0 left-1/4 w-px h-full bg-white/5" />
        <div className="absolute top-0 left-2/4 w-px h-full bg-white/5" />
        <div className="absolute top-0 left-3/4 w-px h-full bg-white/5" />
        <div className="absolute -right-10 top-1/4 font-heading font-bold text-[20rem] leading-none text-brand-green-light opacity-[0.03] select-none">
          G
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container-wide flex-1 flex items-center py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-1.5 gradient-green" />
              <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground">
                Freitag Garten- & Landschaftsbau
              </span>
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] text-foreground mb-6">
              Galabau
              <span className="block text-brand-green-light text-3xl sm:text-4xl lg:text-5xl mt-2">Freitag-Gruppe</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground font-body max-w-xl leading-relaxed mb-10">
              {"Professionelle Gestaltung und Pflege von Au\u00dfenanlagen. Wir schaffen gr\u00fcne R\u00e4ume mit Charakter und Qualit\u00e4t."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button variant="green" size="xl" asChild>
                <a href="#kontakt">
                  Jetzt Anfrage stellen
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button variant="green-outline" size="xl" asChild>
                <a href="#leistungen">Leistungen ansehen</a>
              </Button>
            </div>
          </motion.div>

          {/* Stats card */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="lg:col-span-5 hidden lg:block"
          >
            <div className="bg-card/60 backdrop-blur-md border border-white/10 p-8">
              <div className="h-1 gradient-green mb-6" />
              <div className="space-y-6">
                {[
                  { value: "15+", label: "Jahre Erfahrung" },
                  { value: "200+", label: "G\u00e4rten gestaltet" },
                  { value: "100%", label: "Zufriedenheit" },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center gap-4">
                    <span className="font-heading font-bold text-3xl text-brand-green-light">{stat.value}</span>
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground font-body uppercase tracking-wider">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Mobile stats */}
      <div className="relative z-10 border-t border-border bg-card/80 backdrop-blur-md lg:hidden">
        <div className="container-wide py-6">
          <div className="grid grid-cols-3 gap-8">
            {[
              { value: "15+", label: "Jahre Erfahrung" },
              { value: "200+", label: "G\u00e4rten gestaltet" },
              { value: "100%", label: "Zufriedenheit" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-heading font-bold text-2xl text-brand-green-light">{stat.value}</p>
                <p className="text-xs text-muted-foreground font-body mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* Services */
function GalabauServices() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section-padding bg-background" ref={ref} id="leistungen">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16 max-w-2xl"
        >
          <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
            Unsere Leistungen
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
            Was wir f{"\u00fc"}r Sie leisten.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div key={service.title} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: index * 0.1 }}>
                <Card className="border-0 bg-card h-full">
                  <CardContent className="p-6 md:p-8">
                    <div className="inline-flex items-center justify-center w-10 h-10 bg-brand-green-muted border border-brand-green/20 mb-5">
                      <Icon className="w-5 h-5 text-brand-green" />
                    </div>
                    <h3 className="font-heading font-semibold text-base text-foreground mb-2">{service.title}</h3>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-12 bg-card border border-border p-8 md:p-10 flex flex-col md:flex-row items-center gap-6">
          <Search className="w-8 h-8 text-brand-green shrink-0" />
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-base text-foreground mb-1">Spezielle Gartenw{"\u00fc"}nsche?</h3>
            <p className="text-sm text-muted-foreground font-body">Kontaktieren Sie uns - wir finden die passende L{"\u00f6"}sung.</p>
          </div>
          <Button variant="green" size="default" asChild>
            <a href="#kontakt">Anfrage stellen <ArrowRight className="w-4 h-4" /></a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}

/* Process */
function GalabauProzess() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section-padding bg-background" ref={ref}>
      <div className="container-wide">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="mb-16">
          <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">Unser Prozess</span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground">In f{"\u00fc"}nf Schritten zum Traumgarten.</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-border">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div key={step.title} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: index * 0.12 }}>
                <Card className="border-0 bg-card h-full">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center justify-between mb-5">
                      <span className="font-heading font-bold text-3xl text-foreground/10">{step.number}</span>
                      <div className="p-2 bg-brand-green-muted border border-brand-green/20">
                        <Icon className="w-5 h-5 text-brand-green" />
                      </div>
                    </div>
                    <h3 className="font-heading font-semibold text-sm text-foreground mb-2">{step.title}</h3>
                    <p className="text-xs text-muted-foreground font-body leading-relaxed">{step.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* Reviews */
function GalabauReviews() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
            <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">Bewertungen</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">{"Was sagen Kunden \u00fcber uns?"}</h2>
            <div className="bg-background border border-border p-6 mt-6">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-heading font-bold text-3xl text-foreground">4.9</span>
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(i => (<Star key={i} className="w-4 h-4 fill-brand-orange text-brand-orange" />))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground font-body">Google Bewertungen · Stand Juni 2026</p>
            </div>
          </motion.div>
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
            {reviews.map((review, index) => (
              <motion.div key={review.author} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: index * 0.1 }}>
                <Card className="border-0 border-l-2 border-l-brand-green bg-background h-full">
                  <CardContent className="p-6 md:p-8 flex flex-col h-full">
                    <div className="flex gap-0.5 mb-4">{[1,2,3,4,5].map(i => (<Star key={i} className="w-3 h-3 fill-brand-orange text-brand-orange" />))}</div>
                    <Quote className="w-5 h-5 text-muted-foreground/20 mb-3" />
                    <p className="text-sm text-foreground font-body leading-relaxed mb-6 flex-1">{"\u201E"}{review.quote}{"\u201C"}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-brand-green-muted text-brand-green-light flex items-center justify-center">
                        <span className="font-heading font-bold text-[10px]">{review.author.split(' ').map(n => n[0]).join('')}</span>
                      </div>
                      <div>
                        <p className="font-heading font-semibold text-sm text-foreground">{review.author}</p>
                        <p className="text-xs text-muted-foreground font-body">{review.company}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* Contact */
function GalabauKontakt() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { submitContactForm } = await import("@/lib/contactForm");
    const success = await submitContactForm(formData);
    if (success) setSubmitted(true);
  };

  return (
    <section className="section-padding bg-background" ref={ref} id="kontakt">
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6 }}
            className="lg:col-span-2 bg-card border border-border lg:border-r-0 p-8 md:p-12 flex flex-col">
            <div className="w-12 h-1.5 gradient-green mb-6" />
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground mb-4">Galabau-Anfrage<span className="text-muted-foreground block">stellen.</span></h2>
            <p className="text-sm text-muted-foreground font-body leading-relaxed mb-8">Erz{"\u00e4"}hlen Sie uns von Ihrem Gartenprojekt.</p>
            <div className="space-y-3 mb-8">
              {["Kostenlose Gartenbegehung", "Schnelle R\u00fcckmeldung", "Unverbindlich"].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 gradient-green flex items-center justify-center"><Check className="w-3 h-3 text-foreground" /></div>
                  <span className="text-sm text-foreground font-body">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-auto space-y-4 pt-8 border-t border-border">
              <div className="flex items-center gap-3"><div className="p-2 bg-secondary"><Phone className="w-4 h-4 text-muted-foreground" /></div><div><p className="text-xs text-muted-foreground font-body">Telefon</p><p className="text-sm text-foreground font-body font-medium">+49 (0) 531 87 46 10</p></div></div>
              <div className="flex items-center gap-3"><div className="p-2 bg-secondary"><Mail className="w-4 h-4 text-muted-foreground" /></div><div><p className="text-xs text-muted-foreground font-body">E-Mail</p><p className="text-sm text-foreground font-body font-medium">galabau@freitag-bs.de</p></div></div>
              <div className="flex items-center gap-3"><div className="p-2 bg-secondary"><Clock className="w-4 h-4 text-muted-foreground" /></div><div><p className="text-xs text-muted-foreground font-body">{"\u00d6"}ffnungszeiten</p><p className="text-sm text-foreground font-body font-medium">Mo - Fr: 10:00 - 14:00</p></div></div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }} className="lg:col-span-3">
            {submitted ? (
              <div className="bg-card border border-border p-12 flex flex-col items-center justify-center h-full min-h-[400px]">
                <div className="w-16 h-16 gradient-green flex items-center justify-center mb-6"><Check className="w-8 h-8 text-foreground" /></div>
                <h3 className="font-heading font-bold text-2xl text-foreground mb-3">Vielen Dank!</h3>
                <p className="text-sm text-muted-foreground font-body text-center mb-6">Wir melden uns bald.</p>
                <Button variant="outline" onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", message: "" }); }}>Neue Anfrage</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-card border border-border p-8 lg:p-12 space-y-6 h-full">
                <div><h3 className="font-heading font-bold text-xl text-foreground mb-2">Nachricht senden</h3></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2"><label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Name *</label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-background border-border text-foreground font-body h-11" placeholder="Max Mustermann" /></div>
                  <div className="space-y-2"><label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">E-Mail *</label><Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-background border-border text-foreground font-body h-11" placeholder="max@beispiel.de" /></div>
                </div>
                <div className="space-y-2"><label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Telefon</label><Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-background border-border text-foreground font-body h-11" placeholder="+49 531 87 46 10" /></div>
                <div className="space-y-2"><label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Ihr Gartenprojekt *</label><Textarea required rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="bg-background border-border text-foreground font-body resize-none" placeholder="Beschreiben Sie Ihr Gartenprojekt..." /></div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" required className="mt-1 accent-brand-green" />
                  <span className="text-xs text-muted-foreground font-body leading-relaxed">
                    {"Ich habe die "}
                    <a href="/datenschutz" target="_blank" className="underline text-brand-green-light hover:text-foreground">{"Datenschutzerkl\u00e4rung"}</a>
                    {" gelesen und bin mit der Verarbeitung meiner Daten einverstanden. *"}
                  </span>
                </label>
                <Button variant="green" size="lg" className="w-full" type="submit">Nachricht senden <ArrowRight className="w-4 h-4" /></Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* FAQ */
function GalabauFAQ() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
            <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">H{"\u00e4"}ufige Fragen</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6">FAQ</h2>
            <p className="text-sm text-muted-foreground font-body leading-relaxed">Antworten auf die h{"\u00e4"}ufigsten Fragen zu unseren Galabau-Leistungen.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 }}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-border">
                  <AccordionTrigger className="font-heading font-semibold text-sm text-foreground hover:no-underline py-5">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground font-body leading-relaxed pb-5">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const galabauReferences = [
  { project: "Firmengarten TechPark", scope: "2.500m\u00b2 Gartengestaltung", year: "2024" },
  { project: "Wohnanlage Gr\u00fcnblick", scope: "Au\u00dfenanlagen 18 WE", year: "2024" },
  { project: "Hotelgarten Seeblick", scope: "Terrassen & Bepflanzung", year: "2023" },
  { project: "Gewerbepark Ost", scope: "Pflasterarbeiten & Gr\u00fcnfl\u00e4chen", year: "2023" },
  { project: "Villa Sonnenrain", scope: "Privatgarten mit Teichanlage", year: "2022" },
  { project: "Kita Regenbogen", scope: "Spielfl\u00e4chen & Bepflanzung", year: "2022" },
];

/* Referenzen */
function GalabauReferenzen() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="mb-16 max-w-2xl">
          <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">Referenzen</span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">{"Ausgew\u00e4hlte Projekte."}</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">{"Ein Auszug unserer erfolgreich abgeschlossenen Garten- und Landschaftsbauprojekte."}</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {galabauReferences.map((item, index) => (
            <motion.div key={item.project} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.4, delay: index * 0.08 }}>
              <Card className="border-0 bg-background h-full">
                <CardContent className="p-6 md:p-8">
                  <span className="text-[10px] px-2.5 py-1 font-heading uppercase tracking-wider bg-brand-green-muted text-brand-green-light inline-block mb-4">{item.year}</span>
                  <h3 className="font-heading font-semibold text-base text-foreground mb-2">{item.project}</h3>
                  <p className="text-sm text-muted-foreground font-body">{item.scope}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* SEO Text */
function GalabauSEO() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const seoText = [
    "Freitag Galabau ist Ihr Spezialist f\u00fcr professionellen Garten- und Landschaftsbau in Deutschland. Als Teil der Freitag-Gruppe verbinden wir handwerkliche Exzellenz mit kreativer Gartenplanung \u2013 f\u00fcr Au\u00dfenanlagen, die begeistern und langfristig Wert schaffen.",
    "Unser Leistungsspektrum umfasst Gartengestaltung, Pflasterarbeiten, Bepflanzungskonzepte und die laufende Gr\u00fcnpflege. Ob privater Traumgarten, repr\u00e4sentativer Firmengarten oder \u00f6ffentliche Gr\u00fcnanlage \u2013 wir setzen jedes Projekt mit Leidenschaft und Fachkompetenz um.",
    "Mit \u00fcber 15 Jahren Erfahrung und einem engagierten Team aus Landschaftsg\u00e4rtnern und Planern realisieren wir Ihre gr\u00fcnen Visionen termingerecht und in h\u00f6chster Qualit\u00e4t. Kontaktieren Sie uns f\u00fcr eine kostenlose Gartenbegehung und Beratung.",
  ];

  return (
    <section className="section-padding bg-background" ref={ref}>
      <div className="container-wide">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="max-w-4xl">
          <div className="w-16 h-1 gradient-green mb-8" />
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-foreground mb-6">
            {"Freitag Galabau \u2013 Ihr Partner f\u00fcr professionellen Garten- & Landschaftsbau"}
          </h2>
          <div className="space-y-4">
            {seoText.map((paragraph, index) => (
              <p key={index} className="text-sm text-muted-foreground font-body leading-relaxed">{paragraph}</p>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

